import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ADMIN_API } from '../../context/AdminAuthContext';
import { Users, ShoppingBag, TrendingUp, Clock, CheckCircle, Package, ArrowRight, RotateCw } from 'lucide-react';
import { Link } from 'react-router-dom';

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-gray-800 rounded-2xl p-5 border border-gray-700">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${color}`}>
      <Icon size={22} className="text-white" />
    </div>
    <p className="text-3xl font-black text-white">{value ?? '—'}</p>
    <p className="text-gray-400 text-sm mt-1">{label}</p>
    {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

const AdminDashboardHome = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${ADMIN_API}/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const statusConfig = {
    en_attente: { label: 'En attente', cls: 'bg-yellow-500/20 text-yellow-400' },
    en_fabrication: { label: 'En fabrication', cls: 'bg-blue-500/20 text-blue-400' },
    expediee: { label: 'Expédiée', cls: 'bg-orange-500/20 text-orange-400' },
    livree: { label: 'Livrée', cls: 'bg-green-500/20 text-green-400' },
    annulee: { label: 'Annulée', cls: 'bg-red-500/20 text-red-400' },
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-black text-white">Tableau de bord</h1>
        <button onClick={load} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors">
          <RotateCw size={14} /> Actualiser
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Users} label="Comptes pro" value={stats?.users?.total} sub={`${stats?.users?.pending} en attente`} color="bg-blue-500" />
        <StatCard icon={CheckCircle} label="Comptes validés" value={stats?.users?.approved} color="bg-green-500" />
        <StatCard icon={ShoppingBag} label="Commandes" value={stats?.orders?.total} sub={`${stats?.orders?.pending} en attente`} color="bg-orange-500" />
        <StatCard icon={TrendingUp} label="Chiffre d'affaires HT" value={`${stats?.revenue?.total_ht?.toFixed(0) ?? 0} €`} color="bg-purple-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Users */}
        <div className="bg-gray-800 rounded-2xl border border-gray-700 p-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-bold text-white">Dernières inscriptions</h2>
            <Link to="/admin/users" className="text-orange-400 text-xs hover:text-orange-300 flex items-center gap-1">
              Voir tout <ArrowRight size={12} />
            </Link>
          </div>
          <div className="space-y-3">
            {stats?.recent_users?.length === 0 && (
              <p className="text-gray-500 text-sm">Aucun utilisateur</p>
            )}
            {stats?.recent_users?.map(u => (
              <div key={u.user_id} className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-semibold">{u.prenom} {u.nom}</p>
                  <p className="text-gray-400 text-xs">{u.company_name} — {u.email}</p>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${
                  u.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                  u.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {u.status === 'approved' ? 'Validé' : u.status === 'pending' ? 'En attente' : 'Refusé'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-gray-800 rounded-2xl border border-gray-700 p-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-bold text-white">Dernières commandes</h2>
            <Link to="/admin/orders" className="text-orange-400 text-xs hover:text-orange-300 flex items-center gap-1">
              Voir tout <ArrowRight size={12} />
            </Link>
          </div>
          <div className="space-y-3">
            {stats?.recent_orders?.length === 0 && (
              <p className="text-gray-500 text-sm">Aucune commande</p>
            )}
            {stats?.recent_orders?.map(o => {
              const sc = statusConfig[o.status] || statusConfig.en_attente;
              return (
                <div key={o.order_id} className="flex items-center justify-between">
                  <div>
                    <p className="text-white text-sm font-semibold">{o.order_id}</p>
                    <p className="text-gray-400 text-xs">{o.company_name} — {o.total_ht?.toFixed(2)} € HT</p>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${sc.cls}`}>{sc.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardHome;
